'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertDialog } from '../../../components/common/AlertDialog';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { MarkdownEditor, EditorCoreRef } from '@/components/markdown/MarkdownEditor';
import TitleSelectInput from './TitleSelectInput';
import { articleAiApi, EditArticleResponse, ChatMessage } from '@/app/api/article-ai';
import { tagsApi } from '@/app/api/tags';
import { categoriesApi, Category } from '@/app/api/categories';
import { articlesApi } from '@/app/api/articles';
import { TagIcon, DocumentTextIcon, Cog6ToothIcon, ArrowsRightLeftIcon, ChevronDoubleRightIcon, ChevronDoubleLeftIcon } from '@heroicons/react/24/outline';
import { BotOff, PanelRightClose, PanelRightOpen, Maximize, Minimize, ChevronsLeftRight, ChevronsRightLeft } from 'lucide-react';
import Tooltip from '../../../components/common/Tooltip';
import ArticleMetaDialog from './ArticleMetaDialog';
import TitleDisplay from './TitleDisplay';
import TaggedSelect from '@/components/common/TaggedSelect';
import { ArticleMeta } from '../utils/article-meta';
import { useHistoryManager, useHistoryEventListener } from '../../../hooks/useEditorEvents';
import { useEditor } from '@/context/EditorContext';
import { diff_match_patch, DIFF_INSERT } from 'diff-match-patch';
import { applyReplacement } from '@/utils/apply-replacement';
import { useIsMobile } from '@/hooks/useIsMobile';
import MobileAgentEditor from './MobileAgentEditor';

interface ArticleEditorProps {
  mode: 'create' | 'edit';
  onSave: (articleData: {
    title: string;
    content: string;
    status: string;
    category: string;
    tags: string[];
    meta?: { [key: string]: any };
    connectionIds?: string[];
  }) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  initialTitle?: string;
  initialContent?: string;
  initialStatus?: string;
  initialCategory?: string;
  initialTags?: string[];
  initialSuggestedTitles?: string[];
  initialProfileId?: string;
  onWizardOpen?: () => void;
  defaultToAgentMode?: boolean;
}

export default function ArticleEditor({
  mode,
  onSave,
  onCancel,
  isSubmitting,
  initialTitle = '',
  initialContent = '',
  initialStatus = 'draft',
  initialCategory = '',
  initialTags = [],
  initialSuggestedTitles = [],
  initialProfileId,
  onWizardOpen,
  defaultToAgentMode = false,
}: ArticleEditorProps) {
  const [title, setTitle] = useState(initialTitle || '');
  const [content, setContent] = useState(initialContent);
  const [status, setStatus] = useState(initialStatus);
  const [category, setCategory] = useState(initialCategory);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [suggestedTitles, setSuggestedTitles] = useState<string[]>([...initialSuggestedTitles]);
  const [citations, setCitations] = useState<string[]>([]);
  const [mentions, setMentions] = useState<string[]>([]);
  const isMobile = useIsMobile();

  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTitle, setAlertTitle] = useState('Attention Required');

  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const [searchParams] = useSearchParams();
  const documentId = useMemo(() => searchParams.get('id'), [searchParams]);

  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<{ value: string; label: string }[]>([]);
  const [isLoading, setIsLoading] = useState(mode === 'edit');
  const { isAgentMode, setIsAgentMode } = useEditor();
  const [manualEdit, setManualEdit] = useState<{ oldContent: string; newContent: string } | null>(null);
  const [isMetaDialogOpen, setIsMetaDialogOpen] = useState(false);
  const [isChatPanelVisible, setIsChatPanelVisible] = useState(true);
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  const [transientId, setTransientId] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isWideMode, setIsWideMode] = useState(false);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [hasUnsavedArticleChanges, setHasUnsavedArticleChanges] = useState(false);
  const [showTitleConfirm, setShowTitleConfirm] = useState(false);
  const [aiSuggestedTitle, setAiSuggestedTitle] = useState<string | null>(null);
  const editorRef = useRef<EditorCoreRef>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Use event-based history management - ArticleEditor is the single source of truth
  const {
    updateHistory,
    history,
    historyPointer,
    changeSummaries,
    undo,
    redo,
    resetHistory,
    canUndo,
    canRedo,
    lastChangeSummary,
    nextChangeSummary
  } = useHistoryManager(initialContent, initialTitle);

  // Get previous content from history for diffing
  const previousContent = historyPointer > 0 ? history[historyPointer - 1].content : '';

  // Sync history with backend saves
  useEffect(() => {
    const handleSave = async () => {
      // Only auto-save if we have changes and are in edit mode
      if (mode === 'edit' && documentId) {
        // ... existing save logic ...
      }
    };
  }, [content, title]); // Add title to dependencies if needed

  // Listen for external content updates (e.g. from collaborative editing or other tabs)
  // useEditorEventListener(documentId, (newContent, prevContent) => { // This hook is not defined in the provided context
  //   if (newContent !== content) {
  //     setContent(newContent);
  //     // We might want to update history here too, but be careful of loops
  //   }
  // });

  // Listen for history actions (undo/redo/reset) dispatched from other components
  // useHistoryEventListener(documentId, (type, newContent, summary) => { // This hook is not defined in the provided context
  //   // This listener might be redundant if we use the hook directly, 
  //   // but it's good for cross-component communication if needed.
  // });

  const handleContentUpdate = useCallback((newContent: string, previousContent?: string, changeSummary?: string) => {
    // Update history when content comes from AI edits
    if (previousContent) {
      // Use current title state
      updateHistory(newContent, title, changeSummary || 'AI edit');
    }

    setContent(newContent);

    // Update editor with Vi as current and V(i-1) as previous
    if (editorRef.current?.updateEditorContent) {
      editorRef.current.updateEditorContent(newContent, previousContent || '');
    }
  }, [updateHistory, title]);

  // History handlers to pass to AgentChatPanel via MarkdownEditor
  const handleUndo = useCallback(async () => {
    // Capture current content before undo for diffing
    const contentBeforeUndo = content;

    const snapshot = undo();
    if (snapshot) {
      setContent(snapshot.content);
      // Ensure title is not undefined before setting
      const titleToRestore = snapshot.title || 'Untitled Article';
      setTitle(titleToRestore);

      // Persist changes to backend
      if (mode === 'edit' && documentId) {
        try {
          await articlesApi.updateArticle(documentId, {
            meta: { title: titleToRestore }
          });
          await articlesApi.saveContent(documentId, snapshot.content);
        } catch (error) {
          console.error('Failed to persist undo:', error);
        }
      }

      // Update editor content with previous content for diffing and auto-scroll
      if (editorRef.current?.updateEditorContent) {
        editorRef.current.updateEditorContent(snapshot.content, contentBeforeUndo);
      }
    }
  }, [undo, mode, documentId, content]);

  const handleRedo = useCallback(async () => {
    // Capture current content before redo for diffing
    const contentBeforeRedo = content;

    const snapshot = redo();
    if (snapshot) {
      setContent(snapshot.content);
      // Ensure title is not undefined before setting
      const titleToRestore = snapshot.title || 'Untitled Article';
      setTitle(titleToRestore);

      // Persist changes to backend
      if (mode === 'edit' && documentId) {
        try {
          await articlesApi.updateArticle(documentId, {
            meta: { title: titleToRestore }
          });
          await articlesApi.saveContent(documentId, snapshot.content);
        } catch (error) {
          console.error('Failed to persist redo:', error);
        }
      }

      // Update editor content with previous content for diffing and auto-scroll
      if (editorRef.current?.updateEditorContent) {
        editorRef.current.updateEditorContent(snapshot.content, contentBeforeRedo);
      }
    }
  }, [redo, mode, documentId, content]);

  const handleReset = useCallback(() => {
    resetHistory(content, title);
  }, [resetHistory, content, title]);

  useEffect(() => {
    setContent(initialContent);
    // We should also initialize history here if needed, but useHistoryManager handles initial state
  }, [initialContent]);

  useEffect(() => {
    setSuggestedTitles(Array.isArray(initialSuggestedTitles) ? [...initialSuggestedTitles] : []);
  }, [initialSuggestedTitles]);

  useEffect(() => {
    if (initialTitle && initialTitle.trim() !== '') setTitle(initialTitle);
  }, [initialTitle]);

  useEffect(() => setCategory(initialCategory), [initialCategory]);
  useEffect(() => setTags(initialTags), [initialTags]);

  // Hide footer and prevent body scroll when in agent mode
  useEffect(() => {
    if (isAgentMode) {
      document.body.classList.add('agent-mode-active');
    } else {
      document.body.classList.remove('agent-mode-active');
    }
    // This cleanup only handles the body class on re-renders
    return () => {
      document.body.classList.remove('agent-mode-active');
    };
  }, [isAgentMode]);

  // Listen for fullscreen changes (e.g., user presses ESC to exit fullscreen)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // This effect runs only once on mount and cleans up only on unmount
  useEffect(() => {
    resetHistory(content, title);

    // Set the initial agent mode based on the prop
    if (defaultToAgentMode) {
      setIsAgentMode(true);
    }

    return () => {
      // Reset the global agent mode state when the editor is unmounted
      setIsAgentMode(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultToAgentMode, setIsAgentMode]);


  // Generate UUID for browser compatibility
  const generateUUID = (): string => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for browsers without crypto.randomUUID
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Initialize transient draft when agent mode is activated in create mode
  const initializeTransientDraft = async (): Promise<string> => {
    try {
      const newTransientId = `transient_${generateUUID()}`;
      await articlesApi.initializeTransient(newTransientId, content);
      setTransientId(newTransientId);
      return newTransientId;
    } catch (error) {
      console.error('Failed to initialize transient draft:', error);
      throw error;
    }
  };

  const handleAiEdit = async (instruction: string, messages: ChatMessage[] = [], selection?: { text: string; prefix: string; suffix: string } | null, cursorContext?: { before: string; after: string; currentParagraph: string; paragraphBefore: string; paragraphAfter: string; isEmptyParagraph: boolean; } | null, signal?: AbortSignal): Promise<EditArticleResponse & { oldContent: string }> => {
    // Set loading state to mask the editor
    setIsAiProcessing(true);

    // Strip diffs from messages to reduce payload size
    const messagesForApi = messages.map(({ diff, ...rest }) => rest);
    const effectiveDocumentId = documentId || transientId;
    if (!effectiveDocumentId) {
      throw new Error('Cannot perform AI edit without a document ID.');
    }
    const oldContent = content;

    console.log('[ArticleEditor] handleAiEdit initiated', {
      instruction,
      articleId: effectiveDocumentId,
      selection: selection ? {
        text: JSON.stringify(selection.text),
        prefixSize: selection.prefix.length,
        suffixSize: selection.suffix.length
      } : 'null',
      cursorContext: cursorContext ? {
        currentParagraph: JSON.stringify(cursorContext.currentParagraph),
        isEmpty: cursorContext.isEmptyParagraph
      } : 'null'
    });
    if (!signal) {
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      signal = abortController.signal;
    }

    try {
      const response = await articleAiApi.editArticle({
        articleId: effectiveDocumentId,
        messages: messagesForApi,
        instruction,
        articleTitle: title,
        selection,
        cursorContext,
        profileId: initialProfileId,
      }, signal);

      console.log('[ArticleEditor] AI Response received', {
        hasContentChanges: response.hasContentChanges,
        hasReplacement: !!response.replacement,
        hasContent: !!response.content,
        replacementSnippet: response.replacement ? JSON.stringify(response.replacement.substring(0, 100)) : 'none'
      });

      // If content has changed, determine which mode to use
      if (response.hasContentChanges) {
        let newContent: string | null = null;

        // 1. Try Replacement Logic (FAST PATH)
        if (response.replacement) {
          if (selection) {
            // Standard case: Replace the user's selection
            const patchResult = applyReplacement(oldContent, selection, response.replacement);
            if (patchResult) {
              newContent = patchResult.content;
            }
          } else if (cursorContext && !cursorContext.isEmptyParagraph) {
            // Fallback: Apply replacement to the current paragraph from cursor context
            const currentParagraphSelection = {
              text: cursorContext.currentParagraph, // Use accurate Markdown context
              prefix: cursorContext.paragraphBefore,
              suffix: cursorContext.paragraphAfter
            };
            console.log('[ArticleEditor] Applying replacement to current paragraph (no selection provided)');
            const patchResult = applyReplacement(oldContent, currentParagraphSelection, response.replacement);
            if (patchResult) {
              newContent = patchResult.content;
            }
          }

          if (!newContent) {
            console.warn('[ArticleEditor] Patch strategy failed to produce newContent');
          }
        }

        // 2. Fallback to Full Content (SAFE PATH)
        // If replacement failed or was not provided, but full content is available
        if (!newContent && response.content) {
          console.log('[ArticleEditor] Using full content mode');
          newContent = response.content;
          // Note: When no replacement is provided, updateEditorContent handles scrolling to the first change indicator automatically
        }

        // 3. Unified Update Logic
        if (newContent) {
          console.log('[ArticleEditor] Applying content update', {
            mode: response.replacement && newContent !== response.content ? 'Patch' : 'Full Content',
            contentLength: newContent.length
          });
          // Explicitly save updates to backend (Unified for both paths)
          try {
            await articlesApi.saveContent(effectiveDocumentId, newContent);
          } catch (saveError) {
            console.error('[ArticleEditor] Failed to save content update:', saveError);
          }

          // Update history
          updateHistory(newContent, title, response.changeSummary || 'AI edit');
          setContent(newContent);

          // Update the response object with the final content
          response.content = newContent;

          // Update editor and get rendered text for highlighting
          const renderedTextResult = editorRef.current?.updateEditorContent(newContent, oldContent);

          // Unified Highlighting Logic
          // If we have a replacement text (whether used for patching or just for reference), try to highlight it
          if (renderedTextResult && response.replacement) {
            const { newRenderedText } = renderedTextResult;
            const replacementText = response.replacement;

            // Strip markdown formatting for comparison with rendered text
            const plainReplacement = replacementText
              .replace(/\*\*/g, '')  // Remove bold
              .replace(/\*/g, '')    // Remove italic
              .replace(/_/g, '')     // Remove underline
              .replace(/`/g, '');    // Remove code

            if (newRenderedText.includes(plainReplacement)) {
              setTimeout(() => editorRef.current?.highlightAndScrollTo(plainReplacement), 100);
            } else {
              const firstSentence = plainReplacement.split(/[.!?]/)[0];
              if (firstSentence?.length > 10 && newRenderedText.includes(firstSentence)) {
                setTimeout(() => editorRef.current?.highlightAndScrollTo(firstSentence), 100);
              }
            }
          }
        }
      }

      // Handle title suggestions
      if (response.suggestedTitle) {
        console.log('Setting AI suggested title:', response.suggestedTitle);
        setAiSuggestedTitle(response.suggestedTitle);
        setShowTitleConfirm(true);
      }

      return { ...response, oldContent };
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
        console.log('[ArticleEditor] AI edit aborted by user');
        throw error;
      }
      console.error('Failed to apply AI edit:', error);
      // Re-throw the error so the AgentChatPanel can handle it
      throw error;
    } finally {
      // Always clear the loading state, regardless of success or failure
      setIsAiProcessing(false);
      abortControllerRef.current = null;
    }
  };

  const handleContentBlur = (newContent: string) => {
    // Add to history only when editor loses focus
    if (newContent !== content) {
      setContent(newContent);
      updateHistory(newContent, title, 'manual edit');
      setManualEdit({ oldContent: content, newContent });
    }
  };

  useEffect(() => {
    const fetchDocumentData = async () => {
      if (mode === 'edit' && documentId) {
        setIsLoading(true);
        try {
          const document = await articlesApi.getArticle(documentId);
          const contentBlob = await articlesApi.getArticleContent(documentId);
          const fetchedContent = await contentBlob.text();
          const fetchedTags = await articlesApi.getArticleTags(documentId);

          setTitle(document.meta?.title || '');
          setContent(fetchedContent);
          setStatus(document.state || 'draft');
          setCategory(document.meta?.category || '');
          setTags(fetchedTags || []);
          setSuggestedTitles(ArticleMeta.getSuggestedTitles(document.meta));
          setCitations(document.meta?.citations || []);
          // Safely map mentions to strings if they are objects
          setMentions(document.meta?.mentions?.map((m: any) => typeof m === 'string' ? m : m.name) || []);

          // Reset history with fetched content and title so undo works correctly
          resetHistory(fetchedContent, document.meta?.title || '');
        } catch (error) {
          console.error('Error fetching document:', error);
          alert('Failed to load document. Please try again.');
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchDocumentData();
  }, [mode, documentId]);

  useEffect(() => {
    const fetchAvailableOptions = async () => {
      try {
        const documentTags = (await tagsApi.getTagsByType('documents')) as string[];
        setAvailableTags(documentTags);

        // Load categories from API
        const categoriesData = await categoriesApi.getCategories();
        setAvailableCategories(categoriesData.map(cat => ({
          value: cat.name,
          label: cat.display_name
        })));
      } catch (error) {
        console.error('Error fetching available options:', error);
      }
    };
    fetchAvailableOptions();
  }, []);

  const handleSaveMeta = async () => {
    // For metadata-only updates, save directly without navigating away
    if (mode === 'edit') {
      const urlParams = new URLSearchParams(window.location.search);
      const documentId = urlParams.get('id');

      if (!documentId) {
        setAlertTitle('Save Failed');
        setAlertMessage('Document ID not found');
        setAlertDialogOpen(true);
        return;
      }

      try {
        // Fetch current document to preserve all metadata
        const document = await articlesApi.getArticle(documentId);
        const originalMeta = document.meta || {};

        // Merge metadata
        const updatedMeta = ArticleMeta.mergeMetadata(originalMeta, {
          suggested_titles: suggestedTitles,
          citations: citations,
          mentions: mentions.map(name => ({ name, type: 'Thing' }))
        });
        updatedMeta.title = title;
        updatedMeta.category = category;

        // Update metadata and tags without navigating
        await articlesApi.updateArticle(documentId, {
          meta: updatedMeta,
          state: status,
        });

        await articlesApi.setArticleTags(documentId, tags);

        setIsMetaDialogOpen(false); // Close dialog on success
      } catch (error) {
        console.error('Failed to save metadata:', error);
        setAlertTitle('Save Failed');
        setAlertMessage('There was an error saving the article metadata. Please try again.');
        setAlertDialogOpen(true);
      }
    } else {
      // For create mode, use the original behavior
      const meta = {
        suggested_titles: suggestedTitles,
        citations: citations,
        mentions: mentions.map(name => ({ name, type: 'Thing' }))
      };
      const saveData = { title, content, status, category, tags, meta };

      try {
        await onSave(saveData);
        setIsMetaDialogOpen(false);
      } catch (error) {
        console.error('Failed to save metadata:', error);
        setAlertTitle('Save Failed');
        setAlertMessage('There was an error saving the article metadata. Please try again.');
        setAlertDialogOpen(true);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setAlertTitle('Title Required');
      setAlertMessage('Please enter a title');
      setAlertDialogOpen(true);
      return;
    }

    // Just save
    const meta = {
      suggested_titles: suggestedTitles,
      citations: citations,
      mentions: mentions.map(name => ({ name, type: 'Thing' }))
    };
    const saveData = { title, content, status, category, tags, meta };
    await onSave(saveData);
  };

  const handleGenerateTitles = useCallback(async (prompt: string): Promise<string[]> => {
    try {
      if (!prompt && (!content || content.trim().length < 50)) {
        throw new Error('Content is too short for title generation.');
      }
      const response = await articleAiApi.generateTitles({
        content: prompt || content,
        tags: tags,
        direction: prompt ? 'from_prompt' : 'from_content',
        profileId: initialProfileId,
      });
      const newTitles = response.titles || [];
      if (newTitles.length > 0) {
        setSuggestedTitles((prev) => [...new Set([...prev, ...newTitles])]);
      }
      return newTitles;
    } catch (error) {
      console.error('Error generating titles:', error);
      return [];
    }
  }, [content, tags]);

  const handleTagsChange = (value: string | string[]) => {
    const newTags = Array.isArray(value) ? value : [];
    setTags(newTags);
    const newAvailableTags = [...new Set([...availableTags, ...newTags])];
    setAvailableTags(newAvailableTags);
  };

  const handleCreateTag = (newTag: string) => {
    setTags([...tags, newTag]);
    if (!availableTags.includes(newTag)) {
      setAvailableTags([...availableTags, newTag]);
    }
  };

  const handleTitleChange = async (newTitle: string, shouldUpdateHistory = false) => {
    // If we want to record this in history (e.g. from AI suggestion), do it before state update
    // so we capture the transition from oldTitle -> newTitle
    if (shouldUpdateHistory) {
      const changeSummary = `Title change: "${title}" -> "${newTitle}"`;
      updateHistory(content, newTitle, changeSummary);

      // Also trigger manual edit display in chat
      setManualEdit({
        oldContent: `Title: ${title}`,
        newContent: `Title: ${newTitle}`
      });
    }

    setTitle(newTitle);

    // Auto-save title to backend if we're in edit mode and have a document ID
    if (mode === 'edit' && documentId) {
      try {
        await articlesApi.updateArticle(documentId, {
          meta: { title: newTitle }
        });
      } catch (error) {
        console.error('Failed to save title:', error);
        // Could show a toast notification here if desired
      }
    }
  };

  const handleConfirmTitleChange = async () => {
    if (!aiSuggestedTitle) return;

    // Use handleTitleChange which auto-saves to backend, passing true to update history
    await handleTitleChange(aiSuggestedTitle, true);

    setShowTitleConfirm(false);
    setAiSuggestedTitle(null);
  };

  const handleCancelTitleChange = () => {
    setShowTitleConfirm(false);
    setAiSuggestedTitle(null);
  };

  const tagOptions = availableTags.map((tag) => ({ value: tag, label: tag }));

  if (isLoading && mode === 'edit') {
    return (
      <div className={`flex flex-col items-center justify-center ${isMobile ? 'fixed inset-0 z-[100] bg-white' : 'h-96'}`}>
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-800 font-medium tracking-wide">Loading document...</p>
      </div>
    );
  }

  // Handle agent mode toggle
  const toggleChatPanel = () => setIsChatPanelVisible(!isChatPanelVisible);

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      // Enter fullscreen
      document.documentElement.requestFullscreen?.();
    } else {
      // Exit fullscreen
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  // Handle wide mode toggle
  const toggleWideMode = () => setIsWideMode(!isWideMode);

  const handleToggleAgentMode = async () => {
    if (!isAgentMode && mode === 'create' && !documentId && !transientId) {
      // Initialize transient draft when entering agent mode in create mode
      try {
        await initializeTransientDraft();
      } catch (error) {
        setAlertTitle('Error');
        setAlertMessage('Failed to initialize agent mode. Please try again.');
        setAlertDialogOpen(true);
        return;
      }
    }
    // When entering agent mode, reset history to the current content
    if (!isAgentMode) {
      resetHistory(content, title);
    }
    setIsAgentMode(!isAgentMode);
  };

  const handleCancelClick = () => {
    // Check if there are unsaved changes
    if (hasUnsavedArticleChanges) {
      // Show confirmation dialog
      setConfirmCancelOpen(true);
    } else {
      // No changes, close directly
      onCancel();
    }
  };

  const handleConfirmCancel = () => {
    // User confirmed, close without saving
    onCancel();
  };

  const executeSaveAndClose = async () => {
    // Check if there are unsaved changes by comparing with initial content
    if (hasUnsavedArticleChanges) {
      // Silently save the article without confirmation
      const meta = {
        suggested_titles: suggestedTitles,
        citations: citations,
        mentions: mentions.map(name => ({ name, type: 'Thing' }))
      };
      const saveData = { title, content, status, category, tags, meta };

      try {
        await onSave(saveData);
        // onSave will handle navigation on success
      } catch (error) {
        console.error('Failed to save:', error);
        // Show AlertDialog on error and abort the operation
        setAlertTitle('Save Failed');
        setAlertMessage('There was an error saving the article. Please try again.');
        setAlertDialogOpen(true);
        // Abort - stay in editor
        return;
      }
    } else {
      // No unsaved changes, just close (navigate back)
      onCancel();
    }
  };

  const handleSaveAndClose = async () => {
    await executeSaveAndClose();
  };



  if (isAgentMode) {
    const effectiveArticleId = documentId || transientId;

    if (isMobile) {
      return (
        <MobileAgentEditor
          articleId={effectiveArticleId || undefined}
          articleTitle={title}
          initialContent={content}
          onSave={async (newContent) => {
            if (effectiveArticleId) {
              try {
                await onSave({ title, content: newContent || content, status, category, tags, meta: { suggested_titles: suggestedTitles } });
              } catch (error) {
                console.error('Auto-save failed:', error);
              }
            }
          }}
          onBlur={handleContentBlur}
          onCancel={handleToggleAgentMode}
          onAiEdit={handleAiEdit}
          onContentUpdate={handleContentUpdate}
          previousContent={previousContent}
          isAiProcessing={isAiProcessing}
          canUndo={canUndo}
          canRedo={canRedo}
          lastChangeSummary={lastChangeSummary}
          nextChangeSummary={nextChangeSummary}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onReset={handleReset}
          manualEdit={manualEdit}
          editorCoreRef={editorRef}
          format="markdown"
          onUnsavedChange={setHasUnsavedArticleChanges}
          onTitleChange={handleTitleChange}
        />
      );
    }

    return (
      <div className="fixed inset-0 z-50 bg-gray-100 flex justify-center">
        <AlertDialog isOpen={alertDialogOpen} onClose={() => setAlertDialogOpen(false)} title={alertTitle} message={alertMessage} />
        <ConfirmDialog
          isOpen={showTitleConfirm}
          onClose={handleCancelTitleChange}
          onConfirm={handleConfirmTitleChange}
          title="AI Suggested Title"
          message={`The AI suggested a new title: "${aiSuggestedTitle}". Would you like to use it?`}
          confirmText="Apply Title"
          cancelText="Keep Current"
        />
        <ArticleMetaDialog
          isOpen={isMetaDialogOpen}
          onClose={() => setIsMetaDialogOpen(false)}
          onSave={handleSaveMeta}
          status={status}
          onStatusChange={setStatus}
          category={category}
          onCategoryChange={setCategory}
          tags={tags}
          onTagsChange={handleTagsChange}
          availableTags={tagOptions}
          availableCategories={availableCategories}
          onCreateTag={handleCreateTag}
          citations={citations}
          onCitationsChange={setCitations}
          mentions={mentions}
          onMentionsChange={setMentions}
        />
        <div className={`w-full ${isWideMode ? 'max-w-full px-4' : 'max-w-7xl'} flex flex-col h-full bg-white shadow-lg`}>
          <header className="flex items-center gap-4 p-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex-1 min-w-0 overflow-hidden">
              <TitleDisplay
                title={title}
                onTitleChange={handleTitleChange}
                isEditable={true}
                onEditingStateChange={setIsTitleEditing}
              />
            </div>

            <div className="h-6 w-px bg-gray-300 flex-shrink-0" />

            <div className="flex items-center gap-2 flex-shrink-0">
              <Tooltip content="Manage article meta information" position="bottom">
                <button
                  type="button"
                  onClick={() => setIsMetaDialogOpen(true)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full focus:outline-none"
                >
                  <Cog6ToothIcon className="h-6 w-6" />
                </button>
              </Tooltip>

              <div className="h-4 w-px bg-gray-300" />

              <Tooltip content={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'} position="bottom">
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full focus:outline-none"
                >
                  {isFullscreen ? <Minimize className="h-6 w-6" /> : <Maximize className="h-6 w-6" />}
                </button>
              </Tooltip>

              <Tooltip content={isWideMode ? 'Normal Width' : 'Full Width'} position="bottom">
                <button
                  type="button"
                  onClick={toggleWideMode}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full focus:outline-none"
                >
                  {isWideMode ? <ChevronsRightLeft className="h-6 w-6" /> : <ChevronsLeftRight className="h-6 w-6" />}
                </button>
              </Tooltip>

              <div className="h-4 w-px bg-gray-300" />

              <Tooltip content={isChatPanelVisible ? 'Hide AI Chat' : 'Show AI Chat'} position="bottom">
                <button
                  type="button"
                  onClick={toggleChatPanel}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full focus:outline-none"
                >
                  {isChatPanelVisible ? <PanelRightClose className="h-6 w-6" /> : <PanelRightOpen className="h-6 w-6" />}
                </button>
              </Tooltip>

              <Tooltip content="Exit Agent Mode" position="bottom">
                <button
                  type="button"
                  onClick={handleToggleAgentMode}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full focus:outline-none"
                >
                  <BotOff className="h-6 w-6" />
                </button>
              </Tooltip>

              <div className="h-4 w-px bg-gray-300" />

              <button
                type="button"
                onClick={handleSaveAndClose}
                disabled={isSubmitting}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ${hasUnsavedArticleChanges
                  ? 'bg-orange-500 hover:bg-orange-600 focus:ring-orange-500'
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'}`}
              >
                {isSubmitting
                  ? 'Saving...'
                  : hasUnsavedArticleChanges
                    ? 'Save & Close'
                    : 'Close Editor'}
              </button>
            </div>
          </header>
          <div className="flex-grow overflow-hidden">
            <MarkdownEditor
              editorCoreRef={editorRef}
              articleId={effectiveArticleId || undefined}
              articleTitle={title}
              initialContent={content}
              onBlur={handleContentBlur}
              onSave={async (content) => {
                if (effectiveArticleId) {
                  try {
                    await onSave({ title, content, status, category, tags, meta: { suggested_titles: suggestedTitles } });
                  } catch (error) {
                    console.error('Auto-save failed:', error);
                  }
                }
              }}
              placeholder="Write your article content here..."
              format="markdown"
              isAgentMode={isAgentMode}
              isReadOnly={isTitleEditing}
              onToggleAgentMode={handleToggleAgentMode}
              onUnsavedChange={setHasUnsavedArticleChanges}
              isChatPanelVisible={isChatPanelVisible}
              onToggleChatPanel={toggleChatPanel}
              onAiEdit={handleAiEdit}
              onContentUpdate={handleContentUpdate}
              previousContent={previousContent}
              isAiProcessing={isAiProcessing}
              canUndo={canUndo}
              canRedo={canRedo}
              lastChangeSummary={lastChangeSummary}
              nextChangeSummary={nextChangeSummary}
              onUndo={handleUndo}
              onRedo={handleRedo}
              onReset={handleReset}
              manualEdit={manualEdit}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <AlertDialog isOpen={alertDialogOpen} onClose={() => setAlertDialogOpen(false)} title={alertTitle} message={alertMessage} />
      <ConfirmDialog
        isOpen={confirmCancelOpen}
        onClose={() => setConfirmCancelOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to discard them and leave?"
        confirmText="Discard Changes"
        confirmButtonClassName="bg-red-600 text-white hover:bg-red-700"
      />

      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-2" />
            <h1 className="text-xl font-bold text-gray-900">{mode === 'create' ? 'Create New Article' : 'Edit Article'}</h1>
          </div>
          {mode === 'create' && onWizardOpen && (
            <button type="button" onClick={onWizardOpen} className="inline-flex items-center px-3 py-1.5 border border-blue-500 text-sm font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
              Article Creation Wizard
            </button>
          )}
        </div>
      </div>

      <div className="space-y-6">

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="md:col-span-1">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <TaggedSelect value={category} onChange={(newCategory) => setCategory(Array.isArray(newCategory) ? newCategory[0] : newCategory)} options={availableCategories} placeholder="Select a category..." multiple={false} editable={false} className="min-h-[44px] py-1" />
          </div>
          <div className="md:col-span-3">
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700">Tags</label>
              <span className="text-xs text-gray-500">Tags help categorize your article and make it more discoverable</span>
            </div>
            <TaggedSelect value={tags} onChange={handleTagsChange} options={tagOptions} placeholder="Add tags..." multiple={true} editable={true} className="min-h-[44px] py-1" onCreateOption={handleCreateTag} />
          </div>
        </div>

        <TitleSelectInput title={title} suggestedTitles={suggestedTitles} onTitleChange={setTitle} onGenerateTitles={handleGenerateTitles} onAddSuggestedTitle={(newTitle) => !suggestedTitles.includes(newTitle) && setSuggestedTitles([...suggestedTitles, newTitle])} />

        <div className="mb-6">
          <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">Content</label>
          <div className="border border-gray-300 rounded-md overflow-hidden shadow-sm">
            <MarkdownEditor
              editorCoreRef={editorRef}
              articleId={documentId || undefined}
              initialContent={content}
              onChange={(newContent) => {
                // Only update content state, don't add to history on every keystroke
                setContent(newContent);
              }}
              onBlur={handleContentBlur}
              onSave={async (content) => {
                if (documentId) {
                  try {
                    await onSave({ title, content, status, category, tags, meta: { suggested_titles: suggestedTitles } });
                  } catch (error) {
                    console.error('Auto-save failed:', error);
                  }
                }
              }}
              placeholder="Write your article content here..."
              format="markdown"
              isAgentMode={isAgentMode}
              isReadOnly={isTitleEditing}
              onToggleAgentMode={handleToggleAgentMode}
              onUnsavedChange={setHasUnsavedArticleChanges}
              onAiEdit={handleAiEdit}
              onContentUpdate={handleContentUpdate}
              previousContent={previousContent}
              isAiProcessing={isAiProcessing}
              canUndo={canUndo}
              canRedo={canRedo}
              lastChangeSummary={lastChangeSummary}
              nextChangeSummary={nextChangeSummary}
              onUndo={handleUndo}
              onRedo={handleRedo}
              onReset={handleReset}
              manualEdit={manualEdit}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button type="button" onClick={handleCancelClick} className="inline-flex items-center px-5 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" disabled={isSubmitting}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} className="inline-flex items-center px-5 py-3 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" disabled={isSubmitting}>
            {isSubmitting ? (
              <><div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>{mode === 'create' ? 'Creating...' : 'Saving...'}</>
            ) : (
              mode === 'create' ? 'Create Article' : 'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

